<!--
  AttachmentPicker.vue

  File selection + per-file upload progress for E2EE attachments. Selection is a
  v-model of File[]; the parent (SecretForm) performs the actual encrypt+upload
  AFTER the secret is created, updating `progress` (percent by index) and
  `uploading`. Files are encrypted in the browser before they ever leave it — the
  server only ever receives ciphertext.
-->
<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  const props = withDefaults(
    defineProps<{
      modelValue: File[];
      /** Upload percent (0..100) per file, aligned to modelValue by index. */
      progress?: number[];
      /** True while the parent is uploading; disables editing + shows bars. */
      uploading?: boolean;
      maxFiles?: number;
      maxSizeBytes?: number;
      disabled?: boolean;
      /** Upload error surfaced by the parent (e.g. R2 PUT failed). */
      errorMessage?: string;
    }>(),
    {
      progress: () => [],
      uploading: false,
      maxFiles: 1,
      maxSizeBytes: 26_214_400, // 25 MiB, mirrors the server cap
      disabled: false,
      errorMessage: '',
    }
  );

  const emit = defineEmits<{
    (e: 'update:modelValue', files: File[]): void;
  }>();

  const { t } = useI18n();
  const inputRef = ref<HTMLInputElement | null>(null);
  const dragOver = ref(false);
  const error = ref('');

  // The parent's upload error takes precedence over a local selection error.
  const displayError = computed(() => props.errorMessage || error.value);

  const atLimit = computed(() => props.modelValue.length >= props.maxFiles);
  const canEdit = computed(() => !props.disabled && !props.uploading);

  function humanSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  function addFiles(incoming: FileList | File[]) {
    error.value = '';
    const list = Array.from(incoming);
    const next = [...props.modelValue];
    for (const f of list) {
      if (next.length >= props.maxFiles) {
        error.value = t('web.homepage.attach_limit', { count: props.maxFiles });
        break;
      }
      if (f.size > props.maxSizeBytes) {
        error.value = t('web.homepage.attach_too_large', { size: humanSize(props.maxSizeBytes) });
        continue;
      }
      if (f.size === 0) {
        error.value = t('web.homepage.attach_empty');
        continue;
      }
      next.push(f);
    }
    emit('update:modelValue', next);
  }

  function onInputChange(e: Event) {
    const target = e.target as HTMLInputElement;
    if (target.files?.length) addFiles(target.files);
    // Reset so selecting the same file again re-triggers change.
    target.value = '';
  }

  function onDrop(e: DragEvent) {
    dragOver.value = false;
    if (!canEdit.value) return;
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  }

  function removeAt(index: number) {
    if (!canEdit.value) return;
    const next = props.modelValue.filter((_, i) => i !== index);
    emit('update:modelValue', next);
  }

  function pctFor(index: number): number {
    const p = props.progress[index];
    return typeof p === 'number' ? Math.max(0, Math.min(100, Math.round(p))) : 0;
  }
</script>

<template>
  <div class="flex flex-col gap-2">
    <!-- Drop zone / picker — brand-tinted so it reads as an available action,
         not a disabled hint. -->
    <div
      class="rounded-lg border-2 border-dashed p-3.5 transition-colors"
      :class="[
        dragOver
          ? 'border-brand-500 bg-brand-100/70 dark:bg-brand-500/15'
          : 'border-brand-300 bg-brand-50/60 dark:border-brand-500/40 dark:bg-brand-500/10',
        !canEdit || atLimit ? 'opacity-60' : '',
      ]"
      @dragover.prevent="canEdit && (dragOver = true)"
      @dragleave.prevent="dragOver = false"
      @drop.prevent="onDrop">
      <div class="flex items-center justify-between gap-3">
        <span class="flex items-center gap-2">
          <svg
            class="size-4 shrink-0 text-brand-600 dark:text-brand-400"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true">
            <path
              d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
          </svg>
          <span class="text-xs font-medium text-day-ink dark:text-night-ink">
            {{ t('web.homepage.attach_hint') }}
          </span>
        </span>
        <button
          type="button"
          class="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-brand-500 dark:hover:bg-brand-400"
          :disabled="!canEdit || atLimit"
          :data-testid="'attachment-pick'"
          @click="inputRef?.click()">
          <svg
            class="size-3.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true">
            <path d="M12 5v14M5 12h14" />
          </svg>
          {{ t('web.homepage.attach_button') }}
        </button>
      </div>
      <input
        ref="inputRef"
        type="file"
        class="hidden"
        :disabled="!canEdit || atLimit"
        @change="onInputChange" />
    </div>

    <p
      v-if="displayError"
      class="flex items-start gap-1.5 text-[11px] font-medium text-red-600 dark:text-red-400"
      role="alert">
      <svg
        class="mt-px size-3.5 shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true">
        <circle
          cx="12"
          cy="12"
          r="10" />
        <path d="M12 8v4M12 16h.01" />
      </svg>
      {{ displayError }}
    </p>

    <!-- Selected files + progress -->
    <ul
      v-if="modelValue.length"
      class="flex flex-col gap-1.5">
      <li
        v-for="(file, index) in modelValue"
        :key="`${file.name}-${index}`"
        class="rounded border border-day-line px-2.5 py-1.5 dark:border-white/[0.13]"
        :data-testid="`attachment-item-${index}`">
        <div class="flex items-center justify-between gap-2">
          <span class="flex min-w-0 items-center gap-1.5">
            <svg
              class="size-3 shrink-0 text-day-ink-faint dark:text-night-ink-faint"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true">
              <path
                d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
            <span class="truncate text-[11px] text-day-ink dark:text-night-ink">{{ file.name }}</span>
            <span class="shrink-0 font-mono text-[9px] text-day-ink-faint dark:text-night-ink-faint">
              {{ humanSize(file.size) }}
            </span>
          </span>
          <button
            v-if="canEdit"
            type="button"
            class="shrink-0 text-day-ink-faint transition-colors hover:text-red-600 dark:text-night-ink-faint dark:hover:text-red-400"
            :aria-label="t('web.homepage.attach_remove')"
            :data-testid="`attachment-remove-${index}`"
            @click="removeAt(index)">
            <svg
              class="size-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
          <span
            v-else-if="uploading"
            class="shrink-0 font-mono text-[9px] tracking-[0.06em] uppercase"
            :class="
              pctFor(index) >= 100
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-brand-600 dark:text-brand-500'
            ">
            {{ pctFor(index) >= 100 ? t('web.homepage.attach_done') : t('web.homepage.attach_uploading') }}
          </span>
        </div>
        <!-- Progress bar (only while uploading) -->
        <div
          v-if="uploading"
          class="mt-1 h-1 overflow-hidden rounded-full bg-day-100 dark:bg-night-200"
          role="progressbar"
          :aria-valuenow="pctFor(index)"
          aria-valuemin="0"
          aria-valuemax="100">
          <div
            class="h-full rounded-full bg-brand-500 transition-[width] duration-200"
            :style="{ width: `${pctFor(index)}%` }"></div>
        </div>
      </li>
    </ul>
  </div>
</template>
